import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hook';
import { Button, Card, Descriptions, Grid, Input, Result, Space, Tag, Typography } from 'antd';
import { appConfig } from '../../../common/config';
import { InfoCircleOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import { fetchDeviceAndSettings, updateDeviceSetting, updateDeviceSettings } from './_slice';
import { formatDateTime } from '../../../common/utils';
import { isSystemAdmin } from '../../../common/helper';

interface IUpdateSettingsProps {
  deviceIdOrSerial?: string;
  onSaved?: () => void;
  onCanceled?: () => void;
}

const UpdateDeviceSettings: React.FC<IUpdateSettingsProps> = ({
  deviceIdOrSerial,
  onSaved,
  onCanceled,
}) => {
  const deviceSettingsState = useAppSelector((state) => state.deviceSettings);
  const dispatch = useAppDispatch();
  const [keywords, setKeywords] = React.useState<string|undefined>(deviceIdOrSerial);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const cardSize = isMobile ? 'small' : 'default';

  useEffect(() => {
    // If deviceIdOrSerial change, should change the keywords accordingly
    setKeywords(deviceIdOrSerial);
    dispatch(fetchDeviceAndSettings({deviceIdOrSerial}));
    return () => {
    };

  }, [deviceIdOrSerial, dispatch]);

  const hasSettingErrors = deviceSettingsState.deviceSettings?.some((setting: any) => {
    if (setting.Value?.length > 0) {
      if (setting.Minimum !== undefined && Number(setting.Value) < setting.Minimum) return true;
      if (setting.Maximum !== undefined && Number(setting.Value) > setting.Maximum) return true;
    }
    return false;
  });
  const hasSettingsChanged = deviceSettingsState.deviceSettings?.some((setting: any) => setting.Value !== setting.CurrentValue);
  return (
    <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
      <Card size={cardSize} variant="borderless">
        <Input.Search
          size='large'
          placeholder="Device serial number or ID"
          value={keywords}
          loading={deviceSettingsState.isLoading}
          allowClear
          enterButton
          onSearch={(value) => {
            setKeywords(value);
            dispatch(fetchDeviceAndSettings({deviceIdOrSerial: value.trim()}));
          }}
          onChange={(e) => {
            setKeywords(e.target.value);
          }}
        />
      </Card>
      {
        keywords && !deviceSettingsState.isLoading && !deviceSettingsState.device && (
          <Result
            status="warning"
            title={appConfig.msgs.noDeviceFound}
          />
        )
      }
      {
        deviceSettingsState.device && (
          <>
            <Card size={cardSize} title={<><InfoCircleOutlined/> Device Info</>} variant="borderless">
              <Descriptions>
                <Descriptions.Item label="Customer ID">{deviceSettingsState.device.CustomerId}</Descriptions.Item>
                <Descriptions.Item label="Device ID">{deviceSettingsState.device.DeviceId}</Descriptions.Item>
                <Descriptions.Item label="Serial Number">{deviceSettingsState.device.SerialNumber}</Descriptions.Item>
                <Descriptions.Item label="Device Type">{deviceSettingsState.device.DeviceTypeName}</Descriptions.Item>
                <Descriptions.Item label="Network ID">
                  <Space>
                    <Tag color={appConfig.getNetworkType(deviceSettingsState.device.NetworkType).color} style={{ marginRight: 0 }}>
                      {deviceSettingsState.device.NetworkType}
                    </Tag>
                    {deviceSettingsState.device.NetworkId}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Last Received">{formatDateTime(deviceSettingsState.device.LastReceived)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size={cardSize} title={<><SettingOutlined/> Device Settings</>} variant="borderless">
              <Space direction='vertical' size="large" style={{ display: 'flex' }}>
                {
                  deviceSettingsState.deviceSettings?.map((setting: any, index: number) => {
                    const val = setting.Value;
                    let hasError = false;
                    if (val?.length > 0) {
                      if (setting.Minimum !== undefined && Number(val) < setting.Minimum) {
                        hasError = true;
                      }
                      if (setting.Maximum !== undefined && Number(val) > setting.Maximum) {
                        hasError = true;
                      }
                    }
                    return (
                      <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                          <Input
                            type={ setting.Minimum !== undefined && setting.Maximum !== undefined ? 'number' : 'text' }
                            style={{ width: '400px' }}
                            addonBefore={setting.Name}
                            status={hasError ? 'error' : undefined}
                            value={val}
                            onChange={(e) => {
                              dispatch(updateDeviceSetting({ id: setting.Id, value: e.target.value.trim() }));
                            }}
                          />
                          {
                            setting.Minimum !== undefined && setting.Maximum !== undefined && (
                              <Tag color="orange">Range: {setting.Minimum}-{setting.Maximum}</Tag>
                            )
                          }
                          {
                            setting.DefaultValue !== undefined && (
                              <Tag color="green">Default Value: {setting.DefaultValue}</Tag>
                            )
                          }
                        </div>
                        {<Typography.Text type="secondary">{setting.Description}</Typography.Text>}
                      </div>
                    );
                  })
                }
              </Space>
            </Card>

            <Typography.Text type="warning">{appConfig.msgs.updateSettingsTip}</Typography.Text>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              {
                onCanceled && (
                  <Button
                    size='large'
                    style={{ width: 160 }}
                    onClick={() => {
                      onCanceled();
                    }}
                  >
                    Cancel
                  </Button>
                )
              }
              <Button
                type="primary"
                icon={<SaveOutlined />}
                size='large'
                style={{ width: 160 }}
                disabled={!isSystemAdmin() || hasSettingErrors || !hasSettingsChanged}
                onClick={() => {
                  if (onSaved) {
                    dispatch(updateDeviceSettings());
                    onSaved();
                  }
                }}
              >
                Save
              </Button>
            </div>
          </>
        )
      }
    </Space>
  );
};

export default UpdateDeviceSettings;
